import clsx from 'clsx';
import type { FunctionComponent, ReactNode } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconChevronRightSmall, IconCircleInfo } from '~/ui/core/icons';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';

import { toProfileTaskRichTextHtml } from '../profile-task-rich-text';
import { profileTaskTranslationKeys } from '../profile-task-translations';

export const TaskFieldSection: FunctionComponent<{ readonly children: ReactNode; readonly title: string }> = ({
    children,
    title,
}) => (
    <section className="flex flex-col gap-m">
        <h3 className="m-0 text-body-s font-medium leading-normal text-text-primary">{title}</h3>
        {children}
    </section>
);

type MetaRowInfoTone = 'faint' | 'strong';

const metaRowInfoToneClasses: Record<MetaRowInfoTone, string> = {
    faint: 'text-text-content-faint hover:text-text-secondary',
    strong: 'text-text-secondary hover:text-text-primary',
};

export const TaskMetaRowInfo: FunctionComponent<{
    readonly label: string;
    readonly text: string;
    readonly tone?: MetaRowInfoTone;
}> = ({ label, text, tone = 'strong' }) => {
    const translate = useTranslate();

    return (
        <Tooltip>
            <Tooltip.Trigger
                aria-label={translate(profileTaskTranslationKeys.reasoningInfo, { field: label })}
                className={clsx(
                    'm-0 inline-flex shrink-0 appearance-none items-center justify-center rounded-full border-none bg-transparent p-0',
                    'transition-colors duration-100 ease-in-out',
                    metaRowInfoToneClasses[tone],
                    'focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
                )}
                data-testid="profile-task-meta-info"
                type="button"
            >
                <IconCircleInfo size={16} />
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content side="top">{text}</Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip>
    );
};

export type TaskMetaRow = {
    readonly key: string;
    readonly label: string;
    /** Resolved tooltip copy shown behind the info icon. Omitted rows render no info icon. */
    readonly tooltip?: string;
    readonly value: ReactNode;
};

/** Gray outer card holding a faint heading, with the rows in a raised surface split by hairlines. */
export const TaskAuditBox: FunctionComponent<{
    readonly children: ReactNode;
    readonly testId?: string;
    readonly title: string;
}> = ({ children, testId, title }) => (
    <section
        className="flex flex-col gap-xxs overflow-hidden rounded-xl bg-surface-hover p-xxs shadow-sm"
        data-testid={testId}
    >
        <h3 className="m-0 px-m py-xs text-body-xs font-medium leading-normal text-text-content-subtle">{title}</h3>
        {children}
    </section>
);

export const TASK_AUDIT_BOX_ROWS_CLASS = 'm-0 flex flex-col overflow-hidden rounded-lg bg-surface-raised p-0 shadow-sm';
/** The 24px badge rows set the height; text-only rows reserve the same so every box reads as one grid. */
export const TASK_AUDIT_BOX_ROW_CLASS =
    'flex min-h-[calc(24px+2*var(--spacing-s))] items-center gap-m px-m py-s [&:not(:first-child)]:shadow-[inset_0_1px_0_var(--color-border-soft)]';

/** A hairline above every row of a grouped list but the first. */
export const TASK_LIST_ROW_DIVIDER_CLASS = '[&:not(:first-child)]:shadow-[inset_0_1px_0_var(--color-border-soft)]';

/** Rows in a grouped list: the row whose tile is open stays tinted, the others tint on hover. */
export const taskListRowStateClass = ({
    isInteractive,
    isSelected,
}: {
    readonly isInteractive: boolean;
    readonly isSelected: boolean;
}): string => (isSelected ? 'bg-surface-interactive' : isInteractive ? 'hover:bg-surface-hover' : '');

/** The caret at the end of a grouped-list row that opens a tile; expects a `group` ancestor. */
export const TaskListRowCaret: FunctionComponent<{ readonly isSelected: boolean }> = ({ isSelected }) => (
    <span
        aria-hidden={true}
        className={clsx(
            'flex shrink-0 items-center self-center transition-colors duration-100 ease-in-out group-hover:text-text-primary',
            isSelected ? 'text-text-primary' : 'text-text-content-faint',
        )}
    >
        <IconChevronRightSmall size={20} />
    </span>
);

/**
 * The title of a grouped-list row: a button stretched over the whole row when the row opens a tile, plain text
 * otherwise. Expects a `relative` row.
 */
export const TaskListRowTitle: FunctionComponent<{
    readonly children: ReactNode;
    readonly className: string;
    readonly isSelected: boolean;
    readonly onOpen: (() => void) | undefined;
    readonly testId?: string;
}> = ({ children, className, isSelected, onOpen, testId }) =>
    onOpen === undefined ? (
        <span className={className}>{children}</span>
    ) : (
        <Button
            aria-current={isSelected ? 'true' : undefined}
            className={clsx(
                'm-0 cursor-pointer appearance-none border-none bg-transparent p-0 text-left',
                "after:absolute after:inset-0 after:content-['']",
                'focus-visible:outline-none! focus-visible:after:outline focus-visible:after:outline-1 focus-visible:after:-outline-offset-1 focus-visible:after:outline-focus-ring',
                className,
            )}
            data-testid={testId}
            variant="plain"
            onClick={onOpen}
        >
            {children}
        </Button>
    );

/**
 * A short meta label that explains itself in a tooltip under a heading. It is a button so keyboard
 * users reach the same text, and it sits above a row's stretched link.
 */
export const TaskMetaTooltip: FunctionComponent<{
    readonly children: ReactNode;
    readonly className?: string;
    readonly explanation: ReactNode;
    readonly heading: string;
    readonly testId?: string;
}> = ({ children, className, explanation, heading, testId }) => (
    <Tooltip>
        <Tooltip.Trigger
            className={clsx(
                'relative z-[1] m-0 inline-flex cursor-help appearance-none items-center gap-xs rounded-sm border-none bg-transparent p-0 text-text-muted transition-colors duration-100 ease-in-out hover:text-text-primary focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
                className,
            )}
            data-testid={testId}
            type="button"
        >
            {children}
        </Tooltip.Trigger>
        <Tooltip.Portal>
            <Tooltip.Content align="start" side="bottom" sideOffset={6}>
                <span className="flex flex-col gap-xxs">
                    <span className="font-medium">{heading}</span>
                    {explanation}
                </span>
            </Tooltip.Content>
        </Tooltip.Portal>
    </Tooltip>
);

export const TaskDetailsBox: FunctionComponent<{
    readonly rows: readonly TaskMetaRow[];
    readonly testId?: string;
    /** Defaults to the task's "Details" heading. */
    readonly title?: string;
}> = ({ rows, testId = 'profile-task-meta', title }) => {
    const translate = useTranslate();

    if (rows.length === 0) {
        return null;
    }

    return (
        <TaskAuditBox testId={testId} title={title ?? translate(profileTaskTranslationKeys.details)}>
            <dl className={TASK_AUDIT_BOX_ROWS_CLASS}>
                {rows.map(({ key, label, tooltip, value }) => (
                    <div key={key} className={TASK_AUDIT_BOX_ROW_CLASS}>
                        <dt className="min-w-0 flex-1 truncate text-body-xs font-normal leading-normal text-text-muted">
                            {label}
                        </dt>
                        <dd className="m-0 flex min-w-0 items-center gap-s">
                            {value}
                            {tooltip === undefined ? null : (
                                <TaskMetaRowInfo label={label} text={tooltip} tone="faint" />
                            )}
                        </dd>
                    </div>
                ))}
            </dl>
        </TaskAuditBox>
    );
};

const STEP_TITLE_WEIGHT = { card: 'font-medium', list: 'font-normal' } as const;

const STEP_ROW_INTERACTIVE_CLASS =
    'cursor-pointer transition-colors duration-100 ease-in-out focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-focus-ring';

const toStepRowClassName = ({
    className,
    isInteractive,
    isList,
    isSelected,
}: {
    readonly className: string | undefined;
    readonly isInteractive: boolean;
    readonly isList: boolean;
    readonly isSelected: boolean;
}): string => {
    if (isList) {
        return clsx(
            'group flex gap-m px-m py-m',
            taskListRowStateClass({ isInteractive, isSelected }),
            isInteractive && STEP_ROW_INTERACTIVE_CLASS,
        );
    }

    return clsx(
        className ?? 'flex gap-m rounded-xl px-xl py-xl bg-surface-subtle',
        isInteractive && `${STEP_ROW_INTERACTIVE_CLASS} hover:bg-surface-hover`,
    );
};

/**
 * The step's title and its description are commented on separately, so each is handed to the
 * caller's wrapper to become its own anchor. Without wrappers the row renders as plain text.
 *
 * With `onOpen` the whole card is the click target. The card stays a plain element rather than a
 * button so the comment anchors inside keep their own click handling; they stop propagation.
 */
export const TaskWorkflowStepRow: FunctionComponent<{
    /** Overrides the card's own layout; list rows bring theirs. */
    readonly className?: string;
    readonly description: string | undefined;
    /** Shown as a number badge; the badge is left out without an index. */
    readonly index?: number;
    /** Marks the step whose tile is open; list rows stay tinted for it. */
    readonly isSelected?: boolean;
    /** Rendered under the description, inside the text column. */
    readonly meta?: ReactNode;
    readonly onOpen?: () => void;
    readonly openLabel?: string;
    readonly title: string;
    /** Rendered right of the text column, aligned with the title line. */
    readonly trailing?: ReactNode;
    /** A grey card of its own, or a row of a grouped list that ends in a caret when it opens. */
    readonly variant?: 'card' | 'list';
    readonly wrapDescription?: (content: ReactNode) => ReactNode;
    readonly wrapTitle?: (content: ReactNode) => ReactNode;
}> = ({
    className,
    description,
    index,
    isSelected = false,
    meta,
    onOpen,
    openLabel,
    title,
    trailing,
    variant = 'card',
    wrapDescription,
    wrapTitle,
}) => {
    const isList = variant === 'list';
    const titleContent = (
        <span className={clsx('text-body-s leading-normal text-text-primary', STEP_TITLE_WEIGHT[variant])}>
            {title}
        </span>
    );
    const descriptionContent =
        description === undefined || description.length === 0 ? null : (
            <div
                className="text-xs leading-normal text-text-secondary [&_p]:m-0"
                // biome-ignore lint/security/noDangerouslySetInnerHtml: task rich text is restricted to supported editor tags and stripped of attributes
                dangerouslySetInnerHTML={{ __html: toProfileTaskRichTextHtml(description) }}
            />
        );

    const rowClassName = toStepRowClassName({ className, isInteractive: onOpen !== undefined, isList, isSelected });

    // The row stays a plain container: its parts are comment anchors that take button semantics of
    // their own once they carry a thread, and a button may not contain another one. Pointer users
    // get the whole card, keyboard and screen-reader users get the dedicated control below.
    const interactiveProps =
        onOpen === undefined
            ? {}
            : {
                  onClick: () => {
                      // A click that ends a text selection is a comment gesture, not navigation.
                      if ((globalThis.getSelection()?.toString().length ?? 0) > 0) return;
                      onOpen();
                  },
              };

    return (
        <div
            className={rowClassName}
            data-selected={isSelected ? 'true' : undefined}
            data-testid={onOpen === undefined ? undefined : 'profile-task-workflow-step-open'}
            {...interactiveProps}
        >
            {index === undefined ? null : (
                <span
                    aria-hidden={true}
                    className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border-soft bg-surface-raised text-body-xs font-medium tabular-nums text-text-secondary"
                >
                    {index + 1}
                </span>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-xs">
                {wrapTitle === undefined ? titleContent : wrapTitle(titleContent)}
                {descriptionContent === null || wrapDescription === undefined
                    ? descriptionContent
                    : wrapDescription(descriptionContent)}
                {meta}
            </div>
            {trailing}
            {isList && onOpen !== undefined ? <TaskListRowCaret isSelected={isSelected} /> : null}
            {onOpen === undefined || openLabel === undefined ? null : (
                <button
                    className="sr-only cursor-pointer appearance-none border-none bg-transparent p-0 focus-visible:not-sr-only focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
                    type="button"
                    aria-current={isSelected ? 'true' : undefined}
                    onClick={(event) => {
                        event.stopPropagation();
                        onOpen();
                    }}
                >
                    {openLabel}
                </button>
            )}
        </div>
    );
};
